import { ProviderCaseQueue } from 'type-declarations';
import { getFormattedColumnTime } from './HolidayScheduleDialogUtils';
import { GridColDef } from '@mui/x-data-grid';
import { z } from 'zod';

// Schema setup
export type FormData = {
	recurrenceType: string;
	description: string;
	startTime: string;
	endTime: string;
	startDate?: string;
	endDate?: string;
	dayOfWeek?: string;
	includeWeekends?: boolean; // Tracked val expects string for empty values
	dayOfMonth?: number | string; // Tracked val expects string for empty values
	month?: number | string; // Tracked val expects string for empty values
};

// React hook form default values
export const defaultValues: FormData = {
	startTime: '',
	endTime: '',
	recurrenceType: '',
	description: '',
	startDate: '',
	endDate: '',
	dayOfWeek: '',
	includeWeekends: false,
	dayOfMonth: '',
	month: '',
};

// New record used by <HolidayScheduleDialod /> component
export const newRecord: ProviderCaseQueue = {
	feed: '',
	description: '',
	id: '',
	startTime: '',
	endTime: '',
	recurrenceType: '',
	startDate: '',
	endDate: '',
	dayOfWeek: '',
	includeWeekends: false,
	dayOfMonth: '',
	month: '',
};

// MUI <DataGrid /> column headers w/custom cell rendering for start/end times
export const holidaySchedulesColumns: GridColDef[] = [
	{
		editable: false,
		field: 'description',
		flex: 1,
		headerName: 'Description',
		sortable: false,
	},
	{
		editable: false,
		field: 'startTime',
		flex: 1,
		headerName: 'Start Time',
		sortable: true,
		renderCell: (params) => getFormattedColumnTime(params, 'startTime'),
	},
	{
		editable: false,
		field: 'endTime',
		flex: 1,
		headerName: 'End Time',
		sortable: true,
		renderCell: (params) => getFormattedColumnTime(params, 'endTime'),
	},
	{
		editable: false,
		field: 'recurrenceType',
		flex: 1,
		headerName: 'Recurrence',
		sortable: true,
	},
	{
		editable: false,
		field: 'createdBy',
		flex: 1,
		headerName: 'Created By',
		sortable: true,
	},
];

/**
 * Zod validation schema. Splits z.objects up into shared inputs &
 * unique, then merges them. The z.discriminatedUnion method allows
 * for select rules to be applied depending for input from recurrenceType.
 */
// Shared required form inputs
const baseSchema = z.object({
	description: z.string(),
	startTime: z.string({ message: 'Start time is required.' }),
	endTime: z.string({ message: 'End time is required.' }),
});
// Recurrence type specific for inputs
export const HolidayScheduleSchema = z.discriminatedUnion('recurrenceType', [
	z
		.object({
			recurrenceType: z.literal('SINGLE'),
			startDate: z.string({ message: 'Start date is required' }),
			endDate: z.string({ message: 'End date is required.' }),
		})
		.merge(baseSchema),
	z
		.object({
			recurrenceType: z.literal('DAILY'),
		})
		.merge(baseSchema),
	z.object({
		recurrenceType: z.literal('WEEKLY'),
		dayOfWeek: z.string(),
	}),
	z
		.object({
			recurrenceType: z.literal('MONTHLY'),
			dayOfMonth: z
				.number()
				.min(1, { message: 'Day of Month must be between 1 and 28.' })
				.max(28, { message: 'Day of Month must be between 1 and 28.' }),
		})
		.merge(baseSchema),
	z
		.object({
			recurrenceType: z.literal('YEARLY'),
			dayOfMonth: z
				.number()
				.min(1, { message: 'Day of Month must be between 1 and 28.' })
				.max(28, { message: 'Day of Month must be between 1 and 28.' }),
			month: z.number(),
		})
		.merge(baseSchema),
]);
